import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AdminMapInbox.css';
import { fmtSignalTime, fmtUsd } from '../utils/fmtPrice';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const MAP_INBOX_UI_VERSION = '2026-07-02-platform-health';

export default function AdminMapInboxSection() {
  const [status, setStatus] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [directory, setDirectory] = useState('');
  const [filename, setFilename] = useState('omni-map-active.json');
  const [pollSec, setPollSec] = useState('120');
  const [browseDir, setBrowseDir] = useState('.');
  const [entries, setEntries] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const { data } = await axios.get(`${API_BASE}/multi-asset/admin/map-inbox`, {
        headers: authHeaders(),
      });
      setStatus(data);
      const root = data.inboxRoot || '';
      const dir = data.directory || root;
      setEnabled(!!data.enabled);
      setDirectory(dir === root ? '' : dir);
      setFilename(data.filename || 'omni-map-active.json');
      setPollSec(String(Math.round((data.pollMs || 120000) / 1000)));
      if (data.filename) setSelectedFile(data.filename);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBrowse = useCallback(async (dir = browseDir) => {
    try {
      const { data } = await axios.get(`${API_BASE}/multi-asset/admin/map-inbox/browse`, {
        headers: authHeaders(),
        params: { dir },
      });
      setBrowseDir(data.relativeDir || '.');
      setEntries(data.entries || []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, [browseDir]);

  useEffect(() => {
    load();
    loadBrowse('.');
  }, [load]);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const sec = parseInt(pollSec, 10);
      if (!Number.isFinite(sec) || sec < 30 || sec > 3600) {
        throw new Error('Poll interval must be between 30 and 3600 seconds');
      }
      const { data } = await axios.patch(
        `${API_BASE}/multi-asset/admin/map-inbox`,
        {
          enabled,
          directory: directory.trim() || status?.inboxRoot || '',
          filename,
          pollSec: sec,
        },
        { headers: authHeaders() }
      );
      setStatus(data);
      const root = data.inboxRoot || '';
      const dir = data.directory || root;
      setEnabled(!!data.enabled);
      setDirectory(dir === root ? '' : dir);
      if (data.filename) setFilename(data.filename);
      if (data.pollMs) setPollSec(String(Math.round(data.pollMs / 1000)));
      if (data.filename) setSelectedFile(data.filename);
      setMsg('MAP inbox settings saved.');
      document.getElementById('ma-map-inbox')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      const message = e.response?.data?.error || e.message;
      setErr(message);
      window.alert(`MAP inbox save failed: ${message}`);
    } finally {
      setBusy(false);
    }
  };

  const checkNow = async () => {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/admin/map-inbox/check-now`,
        {},
        { headers: authHeaders() }
      );
      setStatus(data.status);
      if (data.result?.applied) {
        setMsg(
          `Applied MAP` +
            (data.result.concernResolve?.openCount != null
              ? ` · ${data.result.concernResolve.openCount} open concern(s)`
              : '')
        );
      } else {
        setMsg(`Check complete: ${data.result?.reason || 'ok'}`);
      }
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const refreshConcerns = async () => {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/admin/map-inbox/concerns/refresh`,
        { sendEmail: true },
        { headers: authHeaders() }
      );
      setStatus((prev) => ({ ...prev, mapConcerns: data.mapConcerns }));
      setMsg(
        `Concerns refreshed · ${data.mapConcerns?.openCount ?? 0} open` +
          (data.result?.alert?.sent ? ' · email sent' : '')
      );
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const resolveConcern = async (id, statusValue = 'resolved') => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/admin/map-inbox/concerns/${encodeURIComponent(id)}/resolve`,
        { status: statusValue },
        { headers: authHeaders() }
      );
      setStatus((prev) => ({ ...prev, mapConcerns: data.mapConcerns }));
      setMsg(`Concern ${id} marked ${statusValue}.`);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const blockEnterSubmit = (e) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const pickEntry = (entry) => {
    if (entry.isDirectory) {
      loadBrowse(entry.relativePath);
      return;
    }
    if (entry.isJson) {
      setFilename(entry.name);
      setSelectedFile(entry.relativePath);
      setMsg(`Selected file: ${entry.relativePath}`);
    }
  };

  if (loading) return <p className="ma-inbox-loading">Loading MAP inbox…</p>;

  const pendingSyncCount =
    status?.pendingSyncCount ??
    (Array.isArray(status?.pendingSyncSignals) ? status.pendingSyncSignals.length : 0);
  const pendingSyncSignals = status?.pendingSyncSignals || [];
  const platformHealth = status?.platformHealth;
  const healthIssues = platformHealth?.issues || [];
  const healthWarnings = platformHealth?.warnings || [];

  return (
    <section
      id="ma-map-inbox"
      className="ma-admin-card ma-map-inbox-card"
      onKeyDown={blockEnterSubmit}
    >
      <h3>📥 Omni MAP JSON inbox</h3>
      <p className="ma-inbox-build-stamp">UI build: {MAP_INBOX_UI_VERSION}</p>
      {platformHealth && !platformHealth.ok && (
        <div className="ma-inbox-health-alert" role="alert">
          <strong>Platform services need attention</strong>
          <ul>
            {healthIssues.map((item) => (
              <li key={item.code}>{item.message}</li>
            ))}
          </ul>
          {status?.externalWorkerMode && (
            <p className="ma-inbox-health-hint">
              Inbox polling runs in <code>omni-obot-worker</code>, not the API. Ensure{' '}
              <code>pm2 restart omni-obot-worker</code> after backend deploys.
            </p>
          )}
          {platformHealth.smtpConfigured && platformHealth.alertRecipientsConfigured ? (
            <p className="ma-inbox-health-hint">Email alerts are enabled for bot admins when services go offline.</p>
          ) : (
            <p className="ma-inbox-health-hint">
              Configure SMTP + bot-admin emails (or <code>PLATFORM_HEALTH_ALERT_EMAIL</code>) for offline alerts.
            </p>
          )}
        </div>
      )}
      {platformHealth?.ok && healthWarnings.length > 0 && (
        <div className="ma-inbox-health-warn" role="status">
          {healthWarnings.map((item) => (
            <p key={item.code}>{item.message}</p>
          ))}
        </div>
      )}
      <p className="ma-card-desc">
        Drop an approved <code>omni-map-v1</code> JSON file on the VPS. The server polls the path below and
        auto-applies <strong>macroPatch</strong>, <strong>signals</strong>, and{' '}
        <strong>pendingSignals</strong> when the file content changes. Use the same filename each time
        (overwrite) — e.g. <code>omni-map-active.json</code>.
      </p>

      <div
        className={`ma-inbox-pending-sync${pendingSyncCount > 0 ? ' ma-inbox-pending-sync--active' : ''}`}
        role="status"
      >
        <div className="ma-inbox-pending-sync-head">
          <strong>Pending ledger sync</strong>
          <span className={`ma-inbox-pending-sync-count${pendingSyncCount > 0 ? ' warn' : ''}`}>
            {pendingSyncCount}
          </span>
        </div>
        {pendingSyncCount > 0 ? (
          <>
            <p className="ma-inbox-pending-sync-desc">
              Chat-agreed trades not yet confirmed on the Omni ledger. The server re-queues these on every
              poll until positions match — Claude must keep them in <code>pendingSignals[]</code> too.
            </p>
            <ul className="ma-inbox-pending-sync-list">
              {pendingSyncSignals.map((intent) => (
                <li key={intent.intentId || `${intent.symbol}-${intent.action}`}>
                  <code>{intent.symbol}</code>{' '}
                  <span className="ma-inbox-pending-sync-action">{intent.action || 'open'}</span>
                  {intent.sizeUsd != null && (
                    <span className="ma-inbox-pending-sync-meta"> · ${Math.round(intent.sizeUsd).toLocaleString()} margin</span>
                  )}
                  {intent.reason && (
                    <span className="ma-inbox-pending-sync-reason"> — {intent.reason}</span>
                  )}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="ma-inbox-pending-sync-desc ma-inbox-pending-sync-desc--ok">
            All inbox trade intents are confirmed on the ledger.
          </p>
        )}
      </div>

      <div
        className={`ma-inbox-concerns${(status?.mapConcerns?.openCount || 0) > 0 ? ' ma-inbox-concerns--active' : ''}`}
        role="region"
        aria-label="MAP concerns"
      >
        <div className="ma-inbox-pending-sync-head">
          <strong>MAP concerns (Gio notes → decisions)</strong>
          <span className={`ma-inbox-pending-sync-count${(status?.mapConcerns?.openCount || 0) > 0 ? ' warn' : ''}`}>
            {status?.mapConcerns?.openCount || 0}
          </span>
        </div>
        <p className="ma-inbox-pending-sync-desc">
          Claude concern quiz runs automatically <strong>once a week on Sundays</strong> (Chicago time)
          after a MAP import — not on every daily import (keeps API cost down). Evening review Claude
          stays nightly. Use <strong>Refresh concerns now</strong> anytime to force a quiz + email.
          Nothing auto-trades from these options until you resolve/acknowledge here.
          {status?.mapConcerns?.cadence ? (
            <>
              {' '}
              Next auto window: {status.mapConcerns.cadence.nextWindow}
              {status.mapConcerns.lastClaudeAt
                ? ` · last Claude run ${fmtSignalTime(status.mapConcerns.lastClaudeAt)}`
                : ''}
              .
            </>
          ) : null}
        </p>
        <div className="ma-inbox-concerns-actions">
          <button type="button" className="ma-btn secondary" disabled={busy} onClick={refreshConcerns}>
            Refresh concerns now (force Claude)
          </button>
          {status?.mapConcerns?.gioDigest && (
            <button
              type="button"
              className="ma-btn secondary"
              onClick={() => {
                navigator.clipboard?.writeText(status.mapConcerns.gioDigest);
                setMsg('Gio digest copied to clipboard.');
              }}
            >
              Copy Gio digest
            </button>
          )}
        </div>
        {status?.mapConcerns?.gioDigest && (
          <div className="ma-inbox-gio-digest">
            <h4>Pass to Gio</h4>
            <pre>{status.mapConcerns.gioDigest}</pre>
          </div>
        )}
        {(status?.mapConcerns?.concerns || []).filter((c) => c.status === 'open').length === 0 ? (
          <p className="ma-inbox-pending-sync-desc ma-inbox-pending-sync-desc--ok">
            No open MAP concerns.
            {status?.mapConcerns?.updatedAt ? ` Last analysis: ${fmtSignalTime(status.mapConcerns.updatedAt)}.` : ''}
          </p>
        ) : (
          <ul className="ma-inbox-concern-list">
            {(status.mapConcerns.concerns || [])
              .filter((c) => c.status === 'open')
              .map((c) => (
                <li key={c.id} className={`ma-inbox-concern ma-inbox-concern--${c.severity || 'medium'}`}>
                  <div className="ma-inbox-concern-head">
                    <strong>{c.title}</strong>
                    <span className="ma-inbox-concern-sev">{c.severity}</span>
                  </div>
                  {c.evidence?.notesExcerpt && (
                    <p className="ma-inbox-concern-quote">“{c.evidence.notesExcerpt}”</p>
                  )}
                  <p className="ma-inbox-concern-facts">
                    <span>Rules: {c.evidence?.ruleFacts || '—'}</span>
                    <br />
                    <span>Ledger: {c.evidence?.ledgerFacts || '—'}</span>
                  </p>
                  {c.operatorDetail && <p className="ma-inbox-concern-detail">{c.operatorDetail}</p>}
                  {c.gioBrief && (
                    <details className="ma-inbox-concern-gio">
                      <summary>Gio brief (this concern)</summary>
                      <pre>{c.gioBrief}</pre>
                    </details>
                  )}
                  <div className="ma-inbox-concern-options">
                    {(c.options || []).map((o) => (
                      <details key={o.key} className="ma-inbox-concern-option">
                        <summary>
                          Option {o.key} — {o.label}
                        </summary>
                        <pre>{o.operatorText}</pre>
                        {o.provenance && <p className="ma-inbox-concern-prov">Provenance: {o.provenance}</p>}
                      </details>
                    ))}
                  </div>
                  <div className="ma-inbox-concerns-actions">
                    <button
                      type="button"
                      className="ma-btn secondary"
                      disabled={busy}
                      onClick={() => resolveConcern(c.id, 'resolved')}
                    >
                      Mark resolved
                    </button>
                    <button
                      type="button"
                      className="ma-btn secondary"
                      disabled={busy}
                      onClick={() => resolveConcern(c.id, 'dismissed')}
                    >
                      Dismiss
                    </button>
                  </div>
                </li>
              ))}
          </ul>
        )}
        {status?.mapConcerns?.meta?.source && (
          <p className="ma-inbox-meta">
            Analysis source: {status.mapConcerns.meta.source}
            {status.mapConcerns.meta.error ? ` · note: ${status.mapConcerns.meta.error}` : ''}
          </p>
        )}
      </div>

      {err && <div className="error-message ma-admin-error">{err}</div>}
      {msg && <div className="ma-admin-success">{msg}</div>}

      {status?.lastError && (
        <p className="ma-inbox-last-error">Last error: {status.lastError}</p>
      )}
      {status?.lastAppliedAt && (
        <p className="ma-inbox-meta">
          Last applied: {fmtSignalTime(status.lastAppliedAt)}
          {status.lastSummary?.signalsQueued != null &&
            ` · signals ${status.lastSummary.signalsQueued} · macro ${status.lastSummary.macroUpdated ? 'updated' : 'unchanged'}`}
          {status.lastSummary?.pendingSync?.stillPending != null &&
            ` · pending sync ${status.lastSummary.pendingSync.stillPending}`}
          {status.lastSummary?.portfolioUpdated &&
            ` · portfolio ${status.lastSummary.masterEquityUsd != null ? fmtUsd(status.lastSummary.masterEquityUsd) : 'updated'}`}
        </p>
      )}

      {status?.platformState?.omniMaster?.platformOverrides?.count > 0 && (
        <div className="ma-inbox-portfolio" style={{ borderColor: '#b45309' }}>
          <h4 style={{ color: '#f59e0b' }}>
            Platform overrides — ledger disagreed with Gio&apos;s JSON (
            {status.platformState.omniMaster.platformOverrides.count})
          </h4>
          <ul className="ma-inbox-meta" style={{ margin: '6px 0 8px 18px', padding: 0 }}>
            {status.platformState.omniMaster.platformOverrides.items.map((c, i) => (
              <li key={`${c.at}-${i}`} style={{ marginBottom: 4 }}>
                <span style={{ opacity: 0.7 }}>{fmtSignalTime(c.at)} · {c.source}</span> — {c.message}
              </li>
            ))}
          </ul>
          <p className="ma-inbox-meta" style={{ marginBottom: 6 }}>
            These were corrected automatically. The next export already tells Gio&apos;s AI; if you need
            to brief him by hand, copy this:
          </p>
          <button
            type="button"
            className="ma-btn secondary"
            onClick={() => {
              const txt = status.platformState.omniMaster.platformOverrides.promptBlock || '';
              navigator.clipboard?.writeText(txt).then(
                () => setMsg('Override note copied — paste it into Gio\u2019s AI chat.'),
                () => setErr('Clipboard blocked — select the text in the export instead.')
              );
            }}
          >
            Copy note for Gio
          </button>
        </div>
      )}

      {status?.omniPortfolio && (
        <div className="ma-inbox-portfolio">
          <h4>Omni master book (for Claude Section A)</h4>
          <div className="ma-inbox-portfolio-grid">
            <div>
              <span className="label">Equity</span>
              <strong>{fmtUsd(status.omniPortfolio.totalEquityUsd)}</strong>
            </div>
            <div>
              <span className="label">YTD vs $100K</span>
              <strong
                className={
                  status.omniPortfolio.ytdPct >= 0 ? 'ma-stat-positive' : 'ma-stat-negative'
                }
              >
                {status.omniPortfolio.ytdLabel || '—'}
              </strong>
            </div>
            <div>
              <span className="label">Cash deployable</span>
              <strong>
                {status.omniPortfolio.cashDeployableUsd != null
                  ? fmtUsd(status.omniPortfolio.cashDeployableUsd)
                  : '—'}
              </strong>
            </div>
            <div>
              <span className="label">Deploy % frozen</span>
              <strong>
                {status.omniPortfolio.deployPctFrozen != null
                  ? `${(status.omniPortfolio.deployPctFrozen * 100).toFixed(1)}%`
                  : '—'}
              </strong>
            </div>
          </div>
          {!status.omniPortfolio.hasPortfolioSummary && (
            <p className="field-hint">
              Portfolio summary is computed from the platform ledger when inbox files apply, or included in
              the evening macro chart bundle <code>platformState</code>.
            </p>
          )}
          {(status.promptPlatformStateBlock || status.promptPortfolioBlock) && (
            <div className="ma-inbox-prompt-block">
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  navigator.clipboard?.writeText(
                    status.promptPlatformStateBlock || status.promptPortfolioBlock
                  );
                  setMsg('Platform state summary copied — paste into Claude chat.');
                }}
              >
                Copy platform state for Claude
              </button>
              <pre className="ma-inbox-prompt-pre">
                {status.promptPlatformStateBlock || status.promptPortfolioBlock}
              </pre>
            </div>
          )}
        </div>
      )}

      <div className="ma-inbox-form">
        <div className="form-group">
          <label className="ma-checkbox-label">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Enable automatic MAP inbox polling
          </label>
        </div>

        <div className="form-group">
          <label>Folder on VPS (leave blank for default inbox root)</label>
          <input
            type="text"
            value={directory}
            onChange={(e) => setDirectory(e.target.value)}
            placeholder="(default inbox root)"
          />
          <span className="field-hint">Inbox root: {status?.inboxRoot}</span>
        </div>

        <div className="form-group">
          <label>JSON filename (same name every time — overwrite on update)</label>
          <input
            type="text"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            placeholder="omni-map-active.json"
          />
        </div>

        {status?.resolvedPath && (
          <p className="ma-inbox-resolved">
            Watching: <code>{status.resolvedPath}</code>
          </p>
        )}

        <div className="form-group">
          <label>Poll interval (seconds)</label>
          <input
            type="number"
            min={30}
            max={3600}
            value={pollSec}
            onChange={(e) => setPollSec(e.target.value)}
          />
          <span className="field-hint">30s – 1 hour. Default 120s (2 min). Independent of the 30s trading tick.</span>
        </div>

        {selectedFile && (
          <p className="ma-inbox-selected">
            Selected: <code>{selectedFile}</code>
          </p>
        )}

        <div className="ma-inbox-actions">
          <button
            type="button"
            className="primary-btn"
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              save();
            }}
          >
            Save inbox settings
          </button>
          <button
            type="button"
            className="secondary-btn"
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              checkNow();
            }}
          >
            Check file now
          </button>
        </div>
      </div>

      <div className="ma-inbox-browse">
        <div className="ma-inbox-browse-head">
          <strong>Browse inbox folder</strong>
          <button type="button" className="secondary-btn" onClick={() => loadBrowse(browseDir)}>
            Refresh
          </button>
        </div>
        <p className="field-hint">Current: {browseDir} — click a .json file to select it</p>
        <ul className="ma-inbox-file-list">
          {browseDir !== '.' && (
            <li>
              <button type="button" className="ma-inbox-file-row" onClick={() => loadBrowse('.')}>
                .. (root)
              </button>
            </li>
          )}
          {entries.map((entry) => (
            <li key={entry.relativePath}>
              <button
                type="button"
                className={`ma-inbox-file-row${selectedFile === entry.relativePath ? ' selected' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  pickEntry(entry);
                }}
              >
                {entry.isDirectory ? '📁' : '📄'} {entry.name}
                {!entry.isDirectory && entry.size != null && (
                  <span className="ma-inbox-file-meta">{Math.round(entry.size / 1024)} KB</span>
                )}
              </button>
            </li>
          ))}
          {!entries.length && <li className="ma-inbox-empty">No files — create folder on VPS and upload JSON</li>}
        </ul>
      </div>
    </section>
  );
}
